import { Button, Card, SubmitBar, Tabs, Tooltip, type Tab } from "@rothoblaas/design-system";
import { memo, type FC, type RefObject } from "react";
import { useFormContext } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import { RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import {
  GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING,
  GROOVING_TYPE_CODE_WITHOUT_GROOVING,
} from "../../../http/deckDesigner/types/groovingTypes";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import BoardsCard from "./BoardsCard";
import ConnectorsCard from "./ConnectorsCard";
import styles from "./DeckDesigner.module.css";
import {
  BOARDS_FORM_TAB_INDEX,
  DECK_DESIGNER_PREVIEW_TAB_PARAMETER,
  DECK_DESIGNER_RESULTS_TAB_PARAMETER,
  DECK_DESIGNER_TAB_PARAMETER,
  PREVIEW_TAB_INDEX,
  RESULTS_CARD_ID,
  RESULTS_TAB_INDEX,
  type GoToTabs,
} from "./deckDesignerTabs";
import ShapeAndSizeCard, { type SecondaryDimensionsCache } from "./ShapeAndSizeCard";
import SubstructureCard from "./SubstructureCard";
import { useDerivedFormValue } from "../../../hooks/useDerivedFormValue";

type DeckDesignerFieldName = keyof DeckDesignerConfigurationFormValues;

type DeckDesignerFormProps = {
  accentColor: string;
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasSettingsError: boolean;
  isFormReadOnly: boolean;
  cachedSecondaryDimensionsRef: RefObject<SecondaryDimensionsCache>;
  hasVisitedAutoSelectTriangleState: (key: string) => boolean;
  rememberAutoSelectTriangleState: (key: string) => void;
  resetAutoSelectTriangleHistory: () => void;
  fieldsInError: ReadonlySet<string>;
  goToTabs: GoToTabs;
};

const SHAPE_TAB_FIELDS = [
  "shape",
  "base",
  "height",
  "secondaryBase",
  "secondaryHeight",
  "totalHeight",
  "liveLoad",
] as const satisfies readonly DeckDesignerFieldName[];

const BOARDS_TAB_FIELDS = [
  "hasHorizontalBoards",
  "fasteningType",
  "groovingType",
  "gap",
  "boardWidth",
  "boardThickness",
  "groovingHeight",
] as const satisfies readonly DeckDesignerFieldName[];

const CONNECTORS_TAB_FIELDS = [
  "connectorFamily",
  "secondaryItem",
  "connector",
  "connectorSubstructureScrew",
  "connectorBoardScrew",
  "supportFamily",
  "support",
  "supportScrew",
] as const satisfies readonly DeckDesignerFieldName[];

const SUBSTRUCTURE_TAB_FIELDS = [
  "substructureMaterial",
  "substructureMaterialClass",
  "substructure",
  "joistsSpacing",
  "deflection",
] as const satisfies readonly DeckDesignerFieldName[];

const hasTabValidationError = (fields: readonly DeckDesignerFieldName[], fieldsInError: ReadonlySet<string>): boolean =>
  fields.some((fieldName) => fieldsInError.has(fieldName));

const isFieldFilled = (value: unknown): boolean => value != null && value !== "" && !Number.isNaN(value);

// Fields each tab needs filled before the user may move past it. Conditional fields are listed only
// while their card actually shows them (same conditions as ShapeAndSizeCard/BoardsCard). Connectors
// is the last tab, so it gates nothing and needs no entry.
const getRequiredFieldsByTab = (
  shape: string | null | undefined,
  groovingTypeCode: string | null | undefined,
): DeckDesignerFieldName[][] => [
  [
    "shape",
    "base",
    "height",
    ...(shape === RECTANGULAR_SHAPE ? [] : (["secondaryBase", "secondaryHeight"] as const)),
    "totalHeight",
    "slopeDegrees",
    "liveLoad",
  ],
  [
    "fasteningType",
    "groovingType",
    "gap",
    ...(groovingTypeCode === GROOVING_TYPE_CODE_WITHOUT_GROOVING ||
    groovingTypeCode === GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING
      ? []
      : (["groovingHeight"] as const)),
    "boardWidth",
    "boardThickness",
  ],
  ["substructureMaterial", "substructureMaterialClass", "substructure", "joistsSpacing", "deflection"],
];

// The first tab with a required field still empty, -1 when there's none.
const getFirstUnfilledTabIndex = (values: DeckDesignerConfigurationFormValues): number =>
  getRequiredFieldsByTab(values.shape, values.groovingType?.code).findIndex(
    (requiredFields) => !requiredFields.every((fieldName) => isFieldFilled(values[fieldName])),
  );

// The configuration form: its four tabs and the back/next navigation between them. Memoized, with
// stable props from DeckDesigner: it re-renders only when the tabs' completeness or errors change -
// each card re-renders through its own watched fields.
const DeckDesignerForm: FC<DeckDesignerFormProps> = ({
  accentColor,
  defaultValues,
  settings,
  hasSettingsError,
  isFormReadOnly,
  cachedSecondaryDimensionsRef,
  hasVisitedAutoSelectTriangleState,
  rememberAutoSelectTriangleState,
  resetAutoSelectTriangleHistory,
  fieldsInError,
  goToTabs,
}) => {
  const i18n = useI18n();
  const formMethods = useFormContext<DeckDesignerConfigurationFormValues>();
  const firstUnfilledTabIndex = useDerivedFormValue(formMethods, getFirstUnfilledTabIndex);

  const formTabDefinitions = [
    {
      title: i18n.translation.deckDesignerModule.configurations.calculation.shapeAndGeometricData,
      errorFields: SHAPE_TAB_FIELDS,
    },
    { title: i18n.translation.deckDesignerModule.configurations.calculation.boards, errorFields: BOARDS_TAB_FIELDS },
    { title: i18n.translation.deckDesignerModule.substructures.singular, errorFields: SUBSTRUCTURE_TAB_FIELDS },
    { title: i18n.translation.deckDesignerModule.connectors.singular, errorFields: CONNECTORS_TAB_FIELDS },
  ];
  const formTabCount = formTabDefinitions.length;
  const incompleteTabIndex = formTabDefinitions.findIndex(
    ({ errorFields }, tabIndex) =>
      hasTabValidationError(errorFields, fieldsInError) || tabIndex === firstUnfilledTabIndex,
  );
  // A tab can be opened only once every tab before it is complete.
  const lastReachableTabIndex = incompleteTabIndex === -1 ? formTabCount - 1 : incompleteTabIndex;
  const formTabs: Tab[] = formTabDefinitions.map(({ title, errorFields }, tabIndex) => ({
    title,
    hasError: hasTabValidationError(errorFields, fieldsInError),
    disabled: tabIndex > lastReachableTabIndex,
  }));
  // Leaving the boards tab also switches the preview to the side view.
  const goToNextFormTab = (tabIndex: number) =>
    goToTabs({
      [DECK_DESIGNER_TAB_PARAMETER]: tabIndex + 1,
      ...(tabIndex === BOARDS_FORM_TAB_INDEX
        ? {
            [DECK_DESIGNER_RESULTS_TAB_PARAMETER]: RESULTS_TAB_INDEX.preview,
            [DECK_DESIGNER_PREVIEW_TAB_PARAMETER]: PREVIEW_TAB_INDEX.section,
          }
        : {}),
    });
  // Rendered inside each tab (not once below the Tabs) so which buttons show depends only on the tab
  // itself: the selected index from the URL lags behind a tab click, which briefly showed "back" on
  // the first tab and "next" on the last one. Outside the fieldset, so viewers can still navigate.
  const renderFormNavigation = (tabIndex: number) => {
    const canGoToNextFormTab = tabIndex < lastReachableTabIndex;

    return (
      <SubmitBar className={styles.formNavigation}>
        {tabIndex > 0 && (
          <Button
            type="button"
            variant="secondary"
            color="var(--accent-base, var(--red))"
            className={styles.formNavigationBack}
            onClick={() => goToTabs({ [DECK_DESIGNER_TAB_PARAMETER]: tabIndex - 1 })}
          >
            {i18n.translation.common.back}
          </Button>
        )}
        {tabIndex < formTabCount - 1 && (
          <Tooltip
            placement="left"
            className={styles.formNavigationTooltip}
            content={
              canGoToNextFormTab
                ? null
                : i18n.translation.deckDesignerModule.configurations.calculation.completeFieldsToContinue
            }
          >
            <Button
              type="button"
              variant="secondary"
              color="var(--accent-base, var(--red))"
              disabled={!canGoToNextFormTab}
              onClick={() => goToNextFormTab(tabIndex)}
            >
              {i18n.translation.common.next}
            </Button>
          </Tooltip>
        )}
        {/* Last tab: no "next", but a way to the result. scrollIntoView matters when the results Card
            sits below the form (narrow screens); side by side it's already in view and nothing moves. */}
        {tabIndex === formTabCount - 1 && (
          <Button
            type="button"
            variant="primary"
            color="var(--accent-base, var(--red))"
            onClick={() => {
              goToTabs({ [DECK_DESIGNER_RESULTS_TAB_PARAMETER]: RESULTS_TAB_INDEX.billOfQuantities });
              document.getElementById(RESULTS_CARD_ID)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }}
          >
            {i18n.translation.deckDesignerModule.configurations.calculation.showBillOfQuantities}
          </Button>
        )}
      </SubmitBar>
    );
  };

  return (
    <Card style={{ minHeight: 400 }} className={styles.formCard} accentColor="var(--accent-base, var(--red))" shadow>
      <Tabs
        renderMode="eager"
        urlParameter={DECK_DESIGNER_TAB_PARAMETER}
        tabs={formTabs}
        // A disabled fieldset disables every native control in the tab without a viewer
        // (no write permission) touching each field; react-select ignores it, so the
        // cards also pass isDisabled to their Selects.
        contents={[
          <div>
            <fieldset disabled={isFormReadOnly} className={styles.formFieldset}>
              <ShapeAndSizeCard
                accentColor={accentColor}
                defaultValues={defaultValues}
                cachedSecondaryDimensionsRef={cachedSecondaryDimensionsRef}
              />
            </fieldset>
            {renderFormNavigation(0)}
          </div>,
          <div>
            <fieldset disabled={isFormReadOnly} className={styles.formFieldset}>
              <BoardsCard defaultValues={defaultValues} settings={settings} hasError={hasSettingsError} />
            </fieldset>
            {renderFormNavigation(1)}
          </div>,
          <div>
            <fieldset disabled={isFormReadOnly} className={styles.formFieldset}>
              <SubstructureCard
                defaultValues={defaultValues}
                settings={settings}
                hasError={hasSettingsError}
                hasVisitedAutoSelectTriangleState={hasVisitedAutoSelectTriangleState}
                rememberAutoSelectTriangleState={rememberAutoSelectTriangleState}
                resetAutoSelectTriangleHistory={resetAutoSelectTriangleHistory}
              />
            </fieldset>
            {renderFormNavigation(2)}
          </div>,
          <div>
            <fieldset disabled={isFormReadOnly} className={styles.formFieldset}>
              <ConnectorsCard
                defaultValues={defaultValues}
                settings={settings}
                hasError={hasSettingsError}
                hasVisitedAutoSelectTriangleState={hasVisitedAutoSelectTriangleState}
                rememberAutoSelectTriangleState={rememberAutoSelectTriangleState}
                resetAutoSelectTriangleHistory={resetAutoSelectTriangleHistory}
              />
            </fieldset>
            {renderFormNavigation(3)}
          </div>,
        ]}
      />
    </Card>
  );
};

export default memo(DeckDesignerForm);
